import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { installAuthExpiryInterceptor } from '../lib/authInterceptor';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format API errors
const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
    // Session 213.85 — install a global axios 401 interceptor so any
    // background call that 401s clears state + bounces to /login with
    // a "session expired" toast instead of leaving the user staring
    // at unexplained "Failed to save" errors.
    installAuthExpiryInterceptor({
      onSessionExpired: () => setUser(false),
    });
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
      });
      setUser(data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const register = async (email, password, contactName, agencyName, phoneNumber, dbaName, extra = {}) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        {
          email,
          password,
          name: contactName,
          company_name: agencyName,
          dba_name: dbaName || null,
          phone_number: phoneNumber,
          turnstile_token: extra.turnstile_token || null,
          website: extra.website || null,
        },
        { withCredentials: true }
      );
      return { success: true, message: data.message };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
      setUser(false);
    } catch (error) {
      console.error('Logout error:', error);
      setUser(false);
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
